import React, {useContext, useEffect, useState} from "react";
import TrackVASContext from "../../TrackVASContext";
import {PageTitle} from "../../../_metronic/layout/core";
import {useAuth} from "../../modules/auth";
import axios from "axios";
import {ENTITY_ID_ATTENDANCE, ENTITY_ID_BRANCH, ENTITY_ID_WORKER} from "../generic/Constants";
import {useNavigate} from "react-router-dom";
import {DayAttendanceModal} from "./components/DayAttendanceModal";

export function MonthAttendanceView() {
    const myData = useContext(TrackVASContext);
    const {currentUser} = useAuth();
    const navigate = useNavigate();

    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [attendanceData, setAttendanceData] = useState([]);
    const [branches, setBranches] = useState([]);
    const [workers, setWorkers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedDay, setSelectedDay] = useState(null);
    const [showDayModal, setShowDayModal] = useState(false);
    const [workerStats, setWorkerStats] = useState([]);

    const profileBreadCrumbs = [
        {
            title: "Home",
            path: "/dashboard",
            isSeparator: false,
            isActive: false,
        },
        {
            title: "",
            path: "",
            isSeparator: true,
            isActive: false,
        },
        {
            title: "Attendance",
            path: "/attendance/branches",
            isSeparator: false,
            isActive: false,
        },
        {
            title: "",
            path: "",
            isSeparator: true,
            isActive: false,
        },
    ];

    useEffect(() => {
        myData.setBaseData({
            toolBarButtons: [],
        });

        return () => {
            myData.setBaseData({
                toolBarButtons: []
            });
        };
    }, []);

    useEffect(() => {
        if (currentUser?.userId) {
            fetchMonthData();
        }
    }, [currentMonth, currentUser?.userId]);

    const fetchMonthData = async () => {
        setIsLoading(true);
        const apiUrl = process.env.REACT_APP_API_URL;

        try {
            // Get start and end of month
            const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
            const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0, 23, 59, 59);

            // Fetch all attendance records for the month
            const attendanceRes = await axios.get(`${apiUrl}/api/generic/all`, {
                params: {
                    entityId: ENTITY_ID_ATTENDANCE,
                    page: 1,
                    sizePerPage: 10000,
                    currentIndex: 0,
                    sortBy: 'attendanceDate'
                }
            });

            // Filter by month
            const monthAttendance = (attendanceRes.data.entities || []).filter(att => {
                const attDate = new Date(att.attendanceDate);
                return attDate >= startOfMonth && attDate <= endOfMonth;
            });

            setAttendanceData(monthAttendance);

            // Fetch branches
            const branchRes = await axios.get(`${apiUrl}/api/generic/all`, {
                params: {
                    entityId: ENTITY_ID_BRANCH,
                    page: 1,
                    sizePerPage: 500,
                    currentIndex: 0,
                    sortBy: 'name'
                }
            });
            setBranches(branchRes.data.entities || []);

            // Fetch workers
            const workerRes = await axios.get(`${apiUrl}/api/generic/all`, {
                params: {
                    entityId: ENTITY_ID_WORKER,
                    page: 1,
                    sizePerPage: 1000,
                    currentIndex: 0,
                    sortBy: 'name'
                }
            });
            setWorkers(workerRes.data.entities || []);

            // Calculate worker statistics
            calculateWorkerStats(monthAttendance, workerRes.data.entities || []);

        } catch (error) {
            console.error("Error fetching month data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const calculateWorkerStats = (attendance, workersList) => {
        const stats = {};

        attendance.forEach(att => {
            if (!stats[att.workerId]) {
                const worker = workersList.find(w => w.id === att.workerId);
                stats[att.workerId] = {
                    workerId: att.workerId,
                    workerName: att.workerName || worker?.name || 'Unknown',
                    count: 0,
                    dates: []
                };
            }
            stats[att.workerId].count++;
            stats[att.workerId].dates.push(new Date(att.attendanceDate));
        });

        const statsArray = Object.values(stats).sort((a, b) => b.count - a.count);
        setWorkerStats(statsArray);
    };

    const getDaysInMonth = () => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];

        // Add empty cells for days before the month starts
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }

        // Add all days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            days.push(day);
        }

        return days;
    };

    const getAttendanceCountForDay = (day) => {
        if (!day) return 0;

        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const dateToCheck = new Date(year, month, day);
        const startOfDay = new Date(dateToCheck.setHours(0, 0, 0, 0));
        const endOfDay = new Date(dateToCheck.setHours(23, 59, 59, 999));

        return attendanceData.filter(att => {
            const attDate = new Date(att.attendanceDate);
            return attDate >= startOfDay && attDate <= endOfDay;
        }).length;
    };

    const handleDayClick = (day) => {
        if (!day) return;

        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const selectedDate = new Date(year, month, day);

        setSelectedDay(selectedDate);
        setShowDayModal(true);
    };

    const handlePreviousMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    };

    const handleToday = () => {
        setCurrentMonth(new Date());
    };

    const formatMonthYear = () => {
        return currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    };

    const isToday = (day) => {
        if (!day) return false;
        const today = new Date();
        const checkDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
        return checkDate.toDateString() === today.toDateString();
    };

    const isFutureDate = (day) => {
        if (!day) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const checkDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
        checkDate.setHours(0, 0, 0, 0);
        return checkDate > today;
    };

    const days = getDaysInMonth();
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
        <>
            <PageTitle breadcrumbs={profileBreadCrumbs}>Month Attendance View</PageTitle>

            <div className="row g-5">
                {/* Calendar Section */}
                <div className="col-lg-8">
                    <div className="card shadow-sm h-100">
                        <div className="card-header border-0 pt-6">
                            <div className="d-flex flex-wrap align-items-center justify-content-between w-100 gap-3">
                                <h3 className="card-title align-items-start flex-column">
                                    <span className="card-label fw-bold fs-2 mb-1">{formatMonthYear()}</span>
                                    <span className="text-muted mt-1 fw-semibold fs-7">
                                        Total Attendance: {attendanceData.length} records
                                    </span>
                                </h3>
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-sm btn-light-primary"
                                        onClick={handlePreviousMonth}
                                    >
                                        <i className="bi bi-chevron-left"></i>
                                    </button>
                                    <button
                                        className="btn btn-sm btn-primary"
                                        onClick={handleToday}
                                    >
                                        Today
                                    </button>
                                    <button
                                        className="btn btn-sm btn-light-primary"
                                        onClick={handleNextMonth}
                                    >
                                        <i className="bi bi-chevron-right"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="card-body pt-0">
                            {isLoading ? (
                                <div className="text-center py-20">
                                    <span className="spinner-border spinner-border-lg text-primary"></span>
                                    <p className="mt-3 text-gray-600">Loading calendar...</p>
                                </div>
                            ) : (
                                <div className="calendar-grid">
                                    {/* Week day headers */}
                                    <div className="d-grid gap-2 mb-2" style={{gridTemplateColumns: 'repeat(7, 1fr)'}}>
                                        {weekDays.map(day => (
                                            <div key={day} className="text-center fw-bold text-gray-700 py-2">
                                                {day}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Calendar days */}
                                    <div className="d-grid gap-2" style={{gridTemplateColumns: 'repeat(7, 1fr)'}}>
                                        {days.map((day, index) => {
                                            const count = getAttendanceCountForDay(day);
                                            const future = isFutureDate(day);

                                            return (
                                                <div
                                                    key={index}
                                                    className={`calendar-day text-center p-3 rounded border ${
                                                        !day ? 'border-0' :
                                                        future ? 'bg-light-secondary border-secondary cursor-default' :
                                                        isToday(day) ? 'bg-light-primary border-primary' :
                                                        count > 0 ? 'bg-light-success border-success cursor-pointer hover-shadow' :
                                                        'border-gray-300 cursor-pointer hover-shadow'
                                                    }`}
                                                    onClick={() => !future && handleDayClick(day)}
                                                    style={{
                                                        minHeight: '80px',
                                                        cursor: day && !future ? 'pointer' : 'default',
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                >
                                                    {day && (
                                                        <>
                                                            <div className={`fw-bold fs-4 mb-2 ${
                                                                isToday(day) ? 'text-primary' :
                                                                future ? 'text-gray-500' :
                                                                'text-gray-800'
                                                            }`}>
                                                                {day}
                                                            </div>
                                                            {!future && count > 0 && (
                                                                <div className="badge badge-success fw-bold">
                                                                    {count} <i className="bi bi-person-check-fill ms-1"></i>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Worker Statistics & Legend Section */}
                <div className="col-lg-4 d-flex flex-column gap-4">
                    {/* Worker Statistics */}
                    <div className="card shadow-sm flex-grow-1 d-flex flex-column" style={{ minHeight: '400px' }}>
                        <div className="card-header border-0 pt-6 flex-shrink-0">
                            <h3 className="card-title">
                                <span className="card-label fw-bold fs-3 mb-1">Worker Statistics</span>
                            </h3>
                        </div>
                        <div className="card-body pt-0 flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
                            {isLoading ? (
                                <div className="text-center py-10">
                                    <span className="spinner-border text-primary"></span>
                                </div>
                            ) : workerStats.length === 0 ? (
                                <div className="text-center py-10">
                                    <i className="bi bi-inbox fs-1 text-gray-400 mb-3 d-block"></i>
                                    <p className="text-gray-600">No attendance records this month</p>
                                </div>
                            ) : (
                                <div className="d-flex flex-column gap-3">
                                    {workerStats.map((stat, index) => (
                                        <div
                                            key={stat.workerId}
                                            className="d-flex align-items-center justify-content-between p-3 rounded bg-light-primary border border-primary"
                                        >
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="symbol symbol-40px symbol-circle">
                                                    <span className="symbol-label bg-primary text-white fw-bold fs-5">
                                                        {stat.workerName.charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                                <div className="d-flex flex-column">
                                                    <span className="fw-bold text-gray-800 fs-6">
                                                        {stat.workerName}
                                                    </span>
                                                    <span className="text-gray-600 fs-8">
                                                        Worker ID: {stat.workerId}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="text-end">
                                                <div className="badge badge-lg badge-primary fw-bolder fs-4">
                                                    {stat.count}
                                                </div>
                                                <div className="text-gray-600 fs-8 mt-1">
                                                    days
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Legend */}
                    <div className="card shadow-sm flex-shrink-0">
                        <div className="card-body">
                            <h5 className="fw-bold mb-4">Legend</h5>
                            <div className="d-flex flex-column gap-3">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="rounded border border-primary bg-light-primary"
                                         style={{width: '30px', height: '30px'}}></div>
                                    <span className="text-gray-700">Today</span>
                                </div>
                                <div className="d-flex align-items-center gap-3">
                                    <div className="rounded border border-success bg-light-success"
                                         style={{width: '30px', height: '30px'}}></div>
                                    <span className="text-gray-700">Has Attendance</span>
                                </div>
                                <div className="d-flex align-items-center gap-3">
                                    <div className="rounded border border-gray-300"
                                         style={{width: '30px', height: '30px'}}></div>
                                    <span className="text-gray-700">No Attendance</span>
                                </div>
                                <div className="d-flex align-items-center gap-3">
                                    <div className="rounded border border-secondary bg-light-secondary"
                                         style={{width: '30px', height: '30px'}}></div>
                                    <span className="text-gray-700">Future Date</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Back Button */}
            <div className="mt-5">
                <button
                    className="btn btn-secondary"
                    onClick={() => navigate('/attendance/branches')}
                >
                    <i className="bi bi-arrow-left me-2"></i>
                    Back to Attendance
                </button>
            </div>

            {/* Day Details Modal */}
            {showDayModal && selectedDay && (
                <DayAttendanceModal
                    show={showDayModal}
                    onHide={() => setShowDayModal(false)}
                    selectedDate={selectedDay}
                    attendanceData={attendanceData}
                    branches={branches}
                    workers={workers}
                />
            )}

            <style dangerouslySetInnerHTML={{__html: `
                .hover-shadow:hover {
                    box-shadow: 0 0.5rem 1.5rem 0.5rem rgba(0, 0, 0, 0.075);
                    transform: translateY(-2px);
                }
                .calendar-day {
                    position: relative;
                }
            `}} />
        </>
    );
}
